import React, { useState, useEffect, useCallback, useRef } from 'react';
import gsap from 'gsap';
import { useSound } from '../hooks/useSound';
import { flashCorrect, flashWrong, towerWobble, Confetti } from '../lib/animations';
import { GameHeader, QuestionCard, MountainRange, ForestLine, AnimatedCloud, SnowParticles } from '../lib/GameVisuals';
import { Heart, Building, ChevronRight, CheckCircle, XCircle, Shield } from 'lucide-react';

// Exercise type labels for badge display
const EXERCISE_LABELS = {
  article_selection: { label: 'Article', color: '#4C6EF5' },
  preposition_selection: { label: 'Preposition', color: '#4C6EF5' },
  conjugation_drill: { label: 'Conjugation', color: '#E03131' },
  verb_selection: { label: 'Verb', color: '#E03131' },
  auxiliary_selection: { label: 'Auxiliary', color: '#E03131' },
  agreement_selection: { label: 'Agreement', color: '#E03131' },
  tense_sorting: { label: 'Tense', color: '#0CA678' },
  sentence_transformation: { label: 'Transform', color: '#0CA678' },
  word_image_speed_match: { label: 'Vocabulary', color: '#7950F2' },
  comparison_selection: { label: 'Compare', color: '#7950F2' },
};

function TutorialScreen({ onStart, exerciseTypes }) {
  const categories = [];
  const seen = new Set();
  exerciseTypes.forEach(et => {
    const info = EXERCISE_LABELS[et];
    if (info && !seen.has(info.label)) {
      seen.add(info.label);
      categories.push(info);
    }
  });

  return (
    <div data-testid="la-tour-tutorial" style={{
      position: 'fixed', inset: 0, zIndex: 100,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(180deg, #87CEEB 0%, #D4EFFC 40%, #5B8A4E 100%)',
      padding: '2rem',
    }}>
      <div style={{ maxWidth: 720, textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <Building size={44} color="#4C6EF5" />
          <h1 style={{ fontFamily: 'Fredoka', fontSize: '3.2rem', fontWeight: 700, color: '#1A1A2E' }}>
            LA TOUR
          </h1>
        </div>
        <p style={{ fontFamily: 'Nunito', fontSize: '1.5rem', color: '#333', marginBottom: '2rem', fontWeight: 600 }}>
          Build the tallest tower by answering correctly!
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(12,166,120,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <CheckCircle size={40} color="#0CA678" style={{ marginBottom: '0.5rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#0CA678', marginBottom: '0.3rem' }}>
              Correct
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              Stack a new block on the tower
            </div>
          </div>

          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(224,49,49,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <XCircle size={40} color="#E03131" style={{ marginBottom: '0.5rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#E03131', marginBottom: '0.3rem' }}>
              Wrong
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              Lose a life — 3 wrong and the tower collapses!
            </div>
          </div>

          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(245,159,0,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <Shield size={40} color="#F59F00" style={{ marginBottom: '0.5rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#F59F00', marginBottom: '0.3rem' }}>
              Checkpoints
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              Gold blocks every 5 — tower won't collapse below them
            </div>
          </div>
        </div>

        {categories.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.05rem', color: '#555', marginBottom: '0.5rem' }}>
              Question types in this set:
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              {categories.map(c => (
                <span key={c.label} style={{
                  padding: '0.25rem 0.75rem', borderRadius: '0.5rem',
                  background: `${c.color}15`, color: c.color,
                  fontFamily: 'Fredoka', fontWeight: 700, fontSize: '1rem',
                }}>{c.label}</span>
              ))}
            </div>
          </div>
        )}

        <button data-testid="tutorial-start-btn"
          onClick={onStart}
          className="btn-arcade btn-primary"
          style={{ fontSize: '1.8rem', padding: '0 3rem', minHeight: 72, gap: '0.75rem', borderBottomWidth: 8 }}>
          <ChevronRight size={28} /> Let's Go!
        </button>
      </div>
    </div>
  );
}

function TowerScene({ height, maxHeight, towerRef, checkpoint }) {
  // Dynamic block sizing — prevents overflow for large targets
  const blockH = Math.max(18, Math.min(40, Math.floor(380 / maxHeight)));
  const gapSize = blockH > 25 ? 3 : 2;
  const numFontSize = blockH > 30 ? '1rem' : blockH > 22 ? '0.85rem' : '0.7rem';
  const cpFontSize = blockH > 30 ? '1rem' : '0.8rem';

  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      borderRadius: '1.25rem', overflow: 'hidden',
      border: '2px solid rgba(0,0,0,0.08)',
      position: 'relative', margin: '0 0.5rem',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    }}>
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} viewBox="0 0 600 500" preserveAspectRatio="none">
        <defs>
          <linearGradient id="tower-sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#87CEEB" />
            <stop offset="50%" stopColor="#B8E4F9" />
            <stop offset="100%" stopColor="#D4EFFC" />
          </linearGradient>
          <linearGradient id="tower-ground" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#5B8A4E" />
            <stop offset="100%" stopColor="#3D6B37" />
          </linearGradient>
        </defs>
        <rect width="600" height="500" fill="url(#tower-sky)" />
        <circle cx="520" cy="60" r="35" fill="#FFD43B" opacity="0.9" />
        <circle cx="520" cy="60" r="45" fill="#FFD43B" opacity="0.1" />
        <circle cx="520" cy="60" r="55" fill="#FFD43B" opacity="0.05" />
        <MountainRange width={600} baseY={250} peaks={5} maxHeight={100} color="#7C9A6E" />
        <MountainRange width={600} baseY={280} peaks={3} maxHeight={70} color="#5B8A4E" />
        <g transform="translate(0, 310)">
          <ForestLine width={600} treeCount={15} minH={30} maxH={60} />
        </g>
        <rect x="0" y="320" width="600" height="180" fill="url(#tower-ground)" />
        {[30, 80, 150, 250, 350, 420, 500, 560].map((x, i) => (
          <g key={i} transform={`translate(${x}, 325)`}>
            <path d={`M0,0 Q-3,-8 0,-12 Q3,-8 0,0`} fill="#4A7A3E" opacity="0.6" />
            <path d={`M4,0 Q1,-10 4,-14 Q7,-10 4,0`} fill="#3D6B37" opacity="0.5" />
          </g>
        ))}
        <AnimatedCloud x={30} y={40} scale={0.7} speed={55} width={600} />
        <AnimatedCloud x={300} y={25} scale={0.5} speed={70} width={600} />
        <AnimatedCloud x={450} y={55} scale={0.6} speed={60} width={600} />
        <g transform="translate(400, 80)">
          <path d="M0,0 Q-5,-3 -8,0" stroke="#555" strokeWidth="1.5" fill="none" />
          <path d="M0,0 Q5,-3 8,0" stroke="#555" strokeWidth="1.5" fill="none" />
        </g>
        <g transform="translate(420, 90)">
          <path d="M0,0 Q-4,-2 -6,0" stroke="#555" strokeWidth="1" fill="none" />
          <path d="M0,0 Q4,-2 6,0" stroke="#555" strokeWidth="1" fill="none" />
        </g>
      </svg>

      <SnowParticles count={12} />

      {/* Tower building area */}
      <div style={{
        position: 'absolute', bottom: '34%', left: '50%', transform: 'translateX(-50%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        width: 280, zIndex: 10,
      }}>
        {height < maxHeight && (
          <div style={{
            fontSize: '1.05rem', color: 'white', background: 'rgba(0,0,0,0.55)',
            padding: '4px 14px', borderRadius: 12, whiteSpace: 'nowrap',
            fontFamily: 'Fredoka', fontWeight: 600, marginBottom: 8,
          }} data-testid="target-marker">
            Target: {maxHeight}
          </div>
        )}

        <div ref={towerRef} style={{
          display: 'flex', flexDirection: 'column-reverse', gap: gapSize,
          transformOrigin: 'bottom center', minWidth: 120,
        }}>
          {Array.from({ length: height }).map((_, i) => {
            const blockWidth = Math.max(120, 240 - i * 8);
            const isCheckpoint = (i + 1) % 5 === 0;
            const blockColors = [
              { bg: 'linear-gradient(180deg, #C0392B 0%, #96281B 100%)', border: '#7B1F15' },
              { bg: 'linear-gradient(180deg, #2980B9 0%, #1F6091 100%)', border: '#174A70' },
              { bg: 'linear-gradient(180deg, #C0392B 0%, #96281B 100%)', border: '#7B1F15' },
              { bg: 'linear-gradient(180deg, #2980B9 0%, #1F6091 100%)', border: '#174A70' },
            ];
            const bc = blockColors[i % 4];
            return (
              <div key={i} style={{
                width: blockWidth, height: blockH, margin: '0 auto',
                borderRadius: 6, position: 'relative',
                background: isCheckpoint
                  ? 'linear-gradient(180deg, #FFD43B 0%, #F59F00 100%)'
                  : bc.bg,
                boxShadow: isCheckpoint
                  ? '0 4px 16px rgba(245,159,0,0.6), inset 0 2px 0 rgba(255,255,255,0.4)'
                  : `0 3px 8px rgba(0,0,0,0.35), inset 0 2px 0 rgba(255,255,255,0.2)`,
                border: isCheckpoint ? '2px solid #E67700' : `2px solid ${bc.border}`,
                animation: i === height - 1 ? 'block-drop 0.4s ease-out' : undefined,
              }}>
                <div style={{
                  position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: isCheckpoint ? cpFontSize : numFontSize,
                  color: 'white', fontWeight: 800, fontFamily: 'Fredoka',
                  textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                }}>
                  {isCheckpoint ? `★ ${i + 1}` : i + 1}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{
          width: 280, height: 18, marginTop: 3,
          background: 'linear-gradient(180deg, #5C4A3A 0%, #3E2E22 100%)',
          borderRadius: '0 0 8px 8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          border: '2px solid rgba(0,0,0,0.2)',
        }} />
      </div>

      {/* Height display — smartboard sized */}
      <div style={{
        position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
        background: 'rgba(255,255,255,0.92)', borderRadius: '1.5rem', padding: '0.4rem 1.5rem',
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)', backdropFilter: 'blur(8px)',
        border: '2px solid rgba(76,110,245,0.2)', zIndex: 10,
      }}>
        <span className="font-score" style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--primary)' }}>
          {height} / {maxHeight}
        </span>
      </div>

      <style>{`
        @keyframes block-drop {
          0% { transform: translateY(-50px) scale(1.1); opacity: 0.3; }
          60% { transform: translateY(5px) scale(0.98); opacity: 1; }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export function LaTour({ items, settings, onGameEnd }) {
  const { playSound, playGameMusic, stopMusic } = useSound();
  useEffect(() => { playGameMusic('la_tour'); return () => stopMusic(); }, [playGameMusic, stopMusic]);
  const maxHeight = Math.min(settings.target || settings.rounds || 10, items.length);
  const maxLives = 3;

  const [showTutorial, setShowTutorial] = useState(true);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [height, setHeight] = useState(0);
  const [lives, setLives] = useState(maxLives);
  const [checkpoint, setCheckpoint] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const [collapse, setCollapse] = useState(false);
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [totalAnswered, setTotalAnswered] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [timer, setTimer] = useState(settings.timer || 0);
  const [showConfetti, setShowConfetti] = useState(0);
  const streakRef = useRef(0);
  const timerRef = useRef(null);
  const towerRef = useRef(null);
  const gameEndedRef = useRef(false);
  const reviewRef = useRef([]);

  const currentItem = items[currentIdx] || null;
  const exerciseTypes = [...new Set(items.map(i => i.exercise_type).filter(Boolean))];

  // Per-question timer — paused during tutorial
  useEffect(() => {
    if (showTutorial) return;
    if (settings.timer <= 0 || !currentItem) return;
    setTimer(settings.timer);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimer(t => {
        if (t <= 1) { handleTimerExpiry(); return settings.timer; }
        if (t <= 6) playSound('tick');
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [currentIdx, showTutorial]);

  const handleTimerExpiry = useCallback(() => {
    if (feedback || collapse || gameEndedRef.current) return;
    const item = items[currentIdx];
    if (!item) return;
    clearInterval(timerRef.current);
    playSound('wrong');
    const correctOpt = item.options?.find(o => o.correct);
    setFeedback('timeout');
    setCorrectAnswer(correctOpt?.text || '');
    setTotalAnswered(a => a + 1);
    streakRef.current = 0;
    // Track as unanswered in review
    reviewRef.current.push({
      prompt: item.prompt_text_fr || item.prompt_text_en || '?',
      correctAnswer: correctOpt?.text || '—', userAnswer: "Time's up", wasCorrect: false,
      type: EXERCISE_LABELS[item.exercise_type]?.label,
    });
    const newLives = lives - 1;
    setLives(newLives);
    setTimeout(() => {
      setFeedback(null);
      setCorrectAnswer(null);
      if (newLives <= 0) {
        playSound('collapse');
        setCollapse(true);
        if (towerRef.current) {
          const blocks = Array.from(towerRef.current.children);
          gsap.timeline({
            onComplete: () => { setHeight(checkpoint); setLives(maxLives); setCollapse(false); goNext(); },
          }).to(blocks.reverse(), { y: 200, opacity: 0, rotation: () => gsap.utils.random(-25, 25), stagger: 0.05, ease: 'power2.in', duration: 0.3 });
        } else { setHeight(checkpoint); setLives(maxLives); setCollapse(false); goNext(); }
      } else {
        goNext();
      }
    }, 1500);
  }, [feedback, collapse, currentIdx, items, lives, checkpoint, playSound]);

  const goNext = useCallback(() => {
    setFeedback(null);
    setCorrectAnswer(null);
    setCurrentIdx(prev => prev + 1);
  }, []);

  // Skip-to-end: parent fires this when teacher hits "Skip" in the game menu.
  // Mirrors the natural end-of-game logic with whatever progress exists.
  const forceEnd = useCallback(() => {
    if (gameEndedRef.current) return;
    gameEndedRef.current = true;
    clearInterval(timerRef.current);
    setShowConfetti(c => c + 1);
    playSound('win');
    const review = [...reviewRef.current];
    for (let i = review.length; i < items.length; i++) {
      const it = items[i]; if (!it) break;
      const correctOpt = (it.options || []).find(o => o.correct);
      review.push({
        prompt: it.prompt_text_fr || it.prompt_text_en || '?',
        correctAnswer: correctOpt?.text || '—', userAnswer: null, wasCorrect: null,
        type: EXERCISE_LABELS[it.exercise_type]?.label,
      });
    }
    setTimeout(() => onGameEnd({
      team1Score: height, team2Score: 0, totalQuestions: totalAnswered,
      accuracy: totalAnswered > 0 ? (totalCorrect / totalAnswered) * 100 : 0, bestStreak,
      questionReview: review, reviewType: 'qa',
    }), 800);
  }, [height, totalAnswered, totalCorrect, bestStreak, items, onGameEnd, playSound]);

  useEffect(() => {
    const onForceEnd = () => forceEnd();
    window.addEventListener('frenchboard:end-game-now', onForceEnd);
    return () => window.removeEventListener('frenchboard:end-game-now', onForceEnd);
  }, [forceEnd]);

  // Game end check
  useEffect(() => {
    if (gameEndedRef.current || showTutorial) return;
    if (height >= maxHeight || currentIdx >= items.length) {
      gameEndedRef.current = true;
      clearInterval(timerRef.current);
      setShowConfetti(c => c + 1);
      playSound('win');
      // Add unanswered questions
      const review = [...reviewRef.current];
      for (let i = review.length; i < items.length; i++) {
        const it = items[i]; if (!it) break;
        const correctOpt = (it.options || []).find(o => o.correct);
        review.push({
          prompt: it.prompt_text_fr || it.prompt_text_en || '?',
          correctAnswer: correctOpt?.text || '—', userAnswer: null, wasCorrect: null,
          type: EXERCISE_LABELS[it.exercise_type]?.label,
        });
      }
      setTimeout(() => onGameEnd({
        team1Score: height, team2Score: 0, totalQuestions: totalAnswered,
        accuracy: totalAnswered > 0 ? (totalCorrect / totalAnswered) * 100 : 0, bestStreak,
        questionReview: review, reviewType: 'qa',
      }), 1500);
    }
  }, [height, currentIdx, showTutorial]);

  const handleAnswer = useCallback((optIdx, btnEl) => {
    if (feedback || collapse || gameEndedRef.current) return;
    const item = items[currentIdx];
    if (!item) return;
    const isCorrect = item.options?.[optIdx]?.correct;
    setTotalAnswered(a => a + 1);

    if (isCorrect) {
      playSound('drop');
      setTotalCorrect(c => c + 1);
      streakRef.current += 1;
      setBestStreak(b => Math.max(b, streakRef.current));
      setFeedback('correct');
      setCorrectAnswer(null);
      if (btnEl) flashCorrect(btnEl);
      // Track correct for review
      const correctOpt = item.options?.find(o => o.correct);
      reviewRef.current.push({
        prompt: item.prompt_text_fr || item.prompt_text_en || '?',
        correctAnswer: correctOpt?.text || '—', userAnswer: correctOpt?.text || '—', wasCorrect: true,
        type: EXERCISE_LABELS[item.exercise_type]?.label,
      });

      const newHeight = height + 1;
      setHeight(newHeight);

      if (newHeight % 5 === 0) { setCheckpoint(newHeight); playSound('match'); }
      setTimeout(() => { setFeedback(null); setCorrectAnswer(null); if (newHeight < maxHeight) goNext(); }, 600);
    } else {
      playSound('wrong');
      streakRef.current = 0;
      setFeedback('wrong');
      const correctOpt = item.options?.find(o => o.correct);
      setCorrectAnswer(correctOpt?.text || '');
      if (btnEl) flashWrong(btnEl);
      if (towerRef.current) towerWobble(towerRef.current);
      // Track wrong for review
      reviewRef.current.push({
        prompt: item.prompt_text_fr || item.prompt_text_en || '?',
        correctAnswer: correctOpt?.text || '—', userAnswer: item.options?.[optIdx]?.text || '—', wasCorrect: false,
        type: EXERCISE_LABELS[item.exercise_type]?.label,
      });

      const newLives = lives - 1;
      setLives(newLives);

      setTimeout(() => {
        if (newLives <= 0) {
          playSound('collapse');
          setCollapse(true);
          if (towerRef.current) {
            const blocks = Array.from(towerRef.current.children);
            gsap.timeline({
              onComplete: () => {
                setHeight(checkpoint);
                setLives(maxLives);
                setCollapse(false);
                setFeedback(null);
                setCorrectAnswer(null);
              },
            })
              .to(towerRef.current, { rotation: 8, duration: 0.3 })
              .to(blocks, {
                y: () => 100 + Math.random() * 200,
                x: () => (Math.random() - 0.5) * 200,
                rotation: () => Math.random() * 180,
                opacity: 0, duration: 0.6, stagger: 0.02,
              }, '<+=0.1')
              .set(towerRef.current, { rotation: 0 });
          } else {
            setTimeout(() => {
              setHeight(checkpoint);
              setLives(maxLives);
              setCollapse(false);
              setFeedback(null);
              setCorrectAnswer(null);
            }, 1000);
          }
        } else {
          setFeedback(null);
          setCorrectAnswer(null);
          goNext();
        }
      }, 1200);
    }
  }, [currentIdx, feedback, collapse, height, lives, checkpoint, maxHeight, items, playSound, goNext]);

  if (showTutorial) {
    return <TutorialScreen onStart={() => setShowTutorial(false)} exerciseTypes={exerciseTypes} />;
  }

  return (
    <div data-testid="la-tour-game" style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)', overflow: 'hidden' }}>
      <Confetti trigger={showConfetti} />

      <GameHeader title="LA TOUR" icon={<Building size={28} color="var(--primary)" />} color="var(--primary)">
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
          {Array.from({ length: maxLives }).map((_, i) => (
            <Heart key={i} size={36} fill={i < lives ? '#E03131' : 'transparent'} color={i < lives ? '#E03131' : '#CED4DA'} data-testid={`life-${i}`}
              style={{ filter: i < lives ? 'drop-shadow(0 2px 4px rgba(224,49,49,0.4))' : undefined, transition: 'all 0.3s' }} />
          ))}
        </div>
        {settings.timer > 0 && (
          <div className={`font-score ${timer <= 5 ? 'timer-critical' : ''}`}
            style={{ fontSize: '2.2rem', fontWeight: 800, color: timer <= 5 ? 'var(--error)' : 'var(--accent)' }}
            data-testid="tower-timer">{timer}</div>
        )}
        {checkpoint > 0 && (
          <div style={{ fontSize: '1.1rem', color: 'var(--accent)', fontWeight: 700, fontFamily: 'Fredoka' }}
            data-testid="checkpoint-display">
            Checkpoint: {checkpoint}
          </div>
        )}
      </GameHeader>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', padding: '0.5rem' }}>
        <div style={{ flex: 1.2, display: 'flex' }}>
          <TowerScene height={height} maxHeight={maxHeight} towerRef={towerRef} checkpoint={checkpoint} />
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0.75rem 1.5rem' }}>
          {currentItem ? (
            <div style={{ maxWidth: 520 }}>
              {/* Exercise type badge */}
              {EXERCISE_LABELS[currentItem.exercise_type] && (
                <div data-testid="exercise-type-badge" style={{
                  display: 'inline-block', marginBottom: '0.5rem',
                  padding: '0.2rem 0.75rem', borderRadius: '0.5rem',
                  background: `${EXERCISE_LABELS[currentItem.exercise_type].color}12`,
                  color: EXERCISE_LABELS[currentItem.exercise_type].color,
                  fontFamily: 'Fredoka', fontWeight: 700, fontSize: '1.05rem',
                }}>
                  {EXERCISE_LABELS[currentItem.exercise_type].label}
                </div>
              )}

              <QuestionCard promptEn={currentItem.prompt_text_en} promptFr={currentItem.prompt_text_fr} promptImage={currentItem.prompt_image} answer={(currentItem.options || []).find(o => o.correct)?.text} />

              {/* Feedback display */}
              {feedback === 'correct' && (
                <div data-testid="feedback-correct" style={{
                  marginTop: '0.5rem', padding: '0.5rem 1rem', borderRadius: '0.75rem',
                  background: 'rgba(12,166,120,0.1)', border: '2px solid rgba(12,166,120,0.3)',
                  textAlign: 'center', fontFamily: 'Fredoka', fontSize: '1.25rem', fontWeight: 700, color: '#0CA678',
                }}>
                  Correct!
                </div>
              )}
              {feedback === 'wrong' && correctAnswer && (
                <div data-testid="feedback-wrong" style={{
                  marginTop: '0.5rem', padding: '0.5rem 1rem', borderRadius: '0.75rem',
                  background: 'rgba(224,49,49,0.08)', border: '2px solid rgba(224,49,49,0.25)',
                  textAlign: 'center', fontFamily: 'Fredoka', fontSize: '1.15rem', fontWeight: 700, color: '#E03131',
                }}>
                  Answer: <span style={{ color: '#0CA678' }}>{correctAnswer}</span>
                </div>
              )}
              {feedback === 'timeout' && (
                <div data-testid="feedback-timeout" style={{
                  marginTop: '0.5rem', padding: '0.5rem 1rem', borderRadius: '0.75rem',
                  background: 'rgba(224,49,49,0.08)', border: '2px solid rgba(224,49,49,0.25)',
                  textAlign: 'center', fontFamily: 'Fredoka', fontSize: '1.15rem', fontWeight: 700, color: '#E03131',
                }}>
                  Time's up! Answer: <span style={{ color: '#0CA678' }}>{correctAnswer}</span>
                </div>
              )}

              {(currentItem.options || []).some(o => o.image) ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', marginTop: '0.75rem' }}>
                  {(currentItem.options || []).map((opt, idx) => (
                    <button key={`${currentItem.item_id}-${idx}`} data-testid={`tower-option-${idx}`}
                      className="option-btn" onClick={(e) => handleAnswer(idx, e.currentTarget)}
                      disabled={!!feedback || collapse}
                      style={{ padding: '0.4rem', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
                      {opt.image && (
                        <img
                          src={process.env.REACT_APP_IMAGE_BASE_URL ? `${process.env.REACT_APP_IMAGE_BASE_URL}/${opt.image}` : `/assets/content/${opt.image}`}
                          alt="" style={{ width: '100%', maxWidth: 120, height: 85, objectFit: 'contain', borderRadius: '0.5rem' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: '0.75rem' }}>
                  {(currentItem.options || []).map((opt, idx) => (
                    <button key={`${currentItem.item_id}-${idx}`} data-testid={`tower-option-${idx}`}
                      className="option-btn" onClick={(e) => handleAnswer(idx, e.currentTarget)}
                      disabled={!!feedback || collapse}
                      style={{ fontSize: '1.15rem', minHeight: 52 }}>
                      {opt.text}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
